import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api, type Attempt, type Deck } from '../api'
import { ProblemBanner } from '../components/Problem'
import { StatusBadge } from '../components/StatusBadge'

export default function DeckDetail() {
  const { deckId = '' } = useParams()
  const navigate = useNavigate()
  const [deck, setDeck] = useState<Deck | null>(null)
  const [attempts, setAttempts] = useState<Attempt[]>([])
  const [error, setError] = useState<unknown>(null)
  const [missingImages, setMissingImages] = useState<string[]>([])

  const load = useCallback(() => {
    api.getDeck(deckId).then(setDeck).catch(setError)
    api
      .attempts(deckId)
      .then((result) => setAttempts(result.attempts))
      .catch(() => setAttempts([]))
  }, [deckId])

  useEffect(load, [load])

  if (error) return <ProblemBanner error={error} />
  if (!deck) return <p className="muted">Loading…</p>

  const revisable = deck.status === 'ACTIVE' || deck.status === 'FLAGGED'

  return (
    <>
      <h1>
        {deck.title} <StatusBadge status={deck.status} />
      </h1>
      <p className="muted">{deck.description}</p>
      <p className="muted">{deck.cardCount} cards</p>

      <div className="row">
        {revisable ? (
          <>
            <Link className="button primary" to={`/decks/${deck.deckId}/learn`}>
              Learn
            </Link>
            <Link className="button" to={`/decks/${deck.deckId}/test`}>
              Test
            </Link>
          </>
        ) : null}
        {deck.status === 'LOCKED' ? (
          <button onClick={() => api.unlockDeck(deck.deckId).then(load).catch(setError)}>
            Unlock
          </button>
        ) : null}
        {deck.status === 'ARCHIVED' ? (
          <button onClick={() =>
              api
                .unarchiveDeck(deck.deckId)
                .then((restored) => {
                  setMissingImages(restored.missingImages ?? [])
                  load()
                })
                .catch(setError)
            }>
            Restore
          </button>
        ) : null}
        <button
          className="danger"
          onClick={() => {
            if (!confirm(`Delete “${deck.title}”? This cannot be undone.`)) return
            api
              .deleteDeck(deck.deckId)
              .then(() => navigate('/decks'))
              .catch(setError)
          }}
        >
          Delete
        </button>
      </div>

      {missingImages.length > 0 ? (
        <div role="status" className="notice">
          <p>
            Deck restored, but {missingImages.length === 1 ? 'an image was' : 'some images were'}{' '}
            deleted while it was archived. The affected cards are now text only:
          </p>
          <ul>
            {missingImages.map((name) => (
              <li key={name}>{name}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {deck.status === 'LOCKED' ? (
        <p className="muted">
          This deck is locked because your plan no longer covers it. It still counts
          towards your deck allowance — delete a deck you no longer want, then unlock this
          one.
        </p>
      ) : null}

      {attempts.length > 0 ? (
        <>
          <h2>Score history</h2>
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Mode</th>
                <th>Portion</th>
                <th>Score</th>
              </tr>
            </thead>
            <tbody>
              {attempts.map((attempt) => (
                <tr key={attempt.sessionId}>
                  <td>{new Date(attempt.completedAt).toLocaleString()}</td>
                  <td>{attempt.mode}</td>
                  <td>{attempt.portion.replace('_', ' ')}</td>
                  <td>
                    {attempt.correct}/{attempt.total} ({attempt.percent}%)
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </>
  )
}
